import type { CategoryId } from './capabilities';

export interface FlowPattern {
  id: string;
  label: string;
  description: string;
  tags: string[]; // featureCatalog ids
  /** Extra guidance appended to the build seed when this pattern is chosen. */
  seedHint?: string;
}

// Tappable interaction patterns shown in wizard step 2. Each maps to existing
// featureCatalog tags so the discovery/generation scope keeps its buildable
// guardrails with no change to the prompt format.
export const FLOW_PATTERNS: FlowPattern[] = [
  { id: 'logList', label: 'Log entries & see a list', description: 'Add things and review them in a list', tags: ['tracker'] },
  { id: 'trackCharts', label: 'Track over time with charts', description: 'Log values and see progress', tags: ['tracker', 'charts'] },
  { id: 'rateReview', label: 'Rate / review things', description: 'Score and categorize items', tags: ['ratings', 'categories'] },
  { id: 'browseSave', label: 'Browse & save items', description: 'Find and keep a collection', tags: ['categories', 'search'] },
  { id: 'checkinFeed', label: 'Check-in feed', description: 'Share updates in a feed', tags: ['social'] },
  { id: 'formSummary', label: 'Form → summary', description: 'Fill a form and see a summary', tags: ['tracker', 'charts'] },
  { id: 'remindersStreaks', label: 'Reminders / streaks', description: 'Stay on track with reminders', tags: ['reminders', 'tracker'] },
  {
    id: 'landingPage',
    label: 'Landing page / showcase',
    description: 'A view-only page for a business, restaurant, or event',
    tags: ['categories'],
    seedHint:
      'This is a VIEW-ONLY landing page / showcase site (public + web-publishable): build it as a polished marketing page — a hero (image, name, tagline), an About section, the main content (e.g. a menu/catalog/services grouped by category with prices), hours of operation, contact + location, and an optional photo gallery. Make the content entities ownership "public" so everyone and the published website can read them, gate any editing to the owner ($.user.role == "owner"), seed it with realistic example content, and do NOT add favorites, saving, likes, or other interactive actions — it is read-only.',
  },
  {
    id: 'blog',
    label: 'Personal site & blog',
    description: 'A personal/brand homepage with a blog you write and publish',
    tags: ['categories'],
    seedHint:
      'This is a PERSONAL SITE + BLOG (public + web-publishable). Build a "posts" entity ownership "public" with fields { title, excerpt, body (markdown), cover (image url), createdAt (date), published (boolean) } and seed ~3 realistic full posts. Screens: (1) Home/Feed — a hero (name/tagline + a background image), a short About block, then a list of posts sorted createdAt desc (each row: cover image + title + excerpt + formatted date) that navigates to the post detail passing the post id; (2) Post detail — a hero using the post cover, the title + formatted date, then richText bound to $.item.body (the markdown), and a back affordance; read the post via first(where($.data.posts,\'id\',$.params.postId)) so every field renders. Add owner-only authoring (visible: "$.user.role == \'owner\'"): a "Write" screen with a title textInput, an excerpt textInput, a cover image (imagePicker or url), a richTextInput bound to body, and a submitForm to posts. Use appAuth gateActions. Keep the public feed/detail free of like/save/comment buttons — it is a read-only reading experience.',
  },
];

export function tagsForPatterns(ids: string[]): string[] {
  const out = new Set<string>();
  for (const id of ids) {
    const p = FLOW_PATTERNS.find((x) => x.id === id);
    if (p) p.tags.forEach((t) => out.add(t));
  }
  return [...out];
}

// Best-effort archetype for the discovery scope. The goal text carries the real
// domain; this only seeds categoryLabel. Priority: social > analytics (charts) >
// lifestyle (ratings) > productivity (the broadest fallback). Patterns whose tags
// match none of these (e.g. browseSave, remindersStreaks) fall through to productivity.
export function archetypeForPatterns(ids: string[]): CategoryId {
  if (ids.includes('landingPage') || ids.includes('blog')) return 'content';
  const tags = tagsForPatterns(ids);
  if (tags.includes('social')) return 'social';
  if (tags.includes('charts')) return 'analytics';
  if (tags.includes('ratings')) return 'lifestyle';
  return 'productivity';
}
